import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../utils/Api";
import { usePersistentForm } from "../hooks/usePersistentForm";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const ensureSessionText = (value) => {
  if (!value) return "";

  const original = String(value).trim();
  const lower = original.toLowerCase();

  if (lower.includes("photo session")) {
    return original;
  }

  return `${original} photo session`;
};

const getImageCount = (value) => {
  if (!value) return "";

  const match = String(value).match(/\d+/);
  return match ? match[0] : "";
};

const formatEditedImages = (value) => {
  const count = getImageCount(value);

  if (!count) return "";

  return `${count} edited image${Number(count) > 1 ? "s" : ""}`;
};

const FORM_DEFAULTS = {
  name: "",
  email: "",
  phone: "",
  notes: "",
};

export default function Bookings() {
  const { apiFetch } = useApi();
  const { errorMessage, isErrorOpen, showError, closeError } = useErrorModal();

  const timeSlots = useMemo(
    () => [
      "09:00",
      "10:00",
      "11:00",
      "12:00",
      "13:00",
      "14:00",
      "15:00",
      "16:00",
      "17:00",
    ],
    [],
  );

  const [sessions, setSessions] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState("");
  const [sessionId, setSessionId] = useState(null);

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  const [unavailableTimes, setUnavailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const { form, updateField } = usePersistentForm({
    scope: "public-site",
    formId: "studio-session-booking-details",
    version: "v1",
    initialValues: FORM_DEFAULTS,
    persistFields: ["name", "email", "phone"],
    ttlMs: 1000 * 60 * 60 * 24 * 45,
    debounceMs: 300,
  });

  const loadPackages = useCallback(async () => {
    try {
      setLoadingPackages(true);
      setPackagesError("");

      const res = await apiFetch("get_studio_packages");

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load studio packages.");
      }

      const mappedPackages = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          name: item.name,
          duration: ensureSessionText(item.session_duration),
          durationHours: Number(item.duration_hours || 0),
          editedImages: formatEditedImages(item.edited_images),
          extra: item.extra,
          price: Number(item.price),
        }),
      );

      setSessions(mappedPackages);
      setSessionId((prev) =>
        prev && mappedPackages.some((pkg) => pkg.id === Number(prev))
          ? Number(prev)
          : mappedPackages.length
            ? mappedPackages[0].id
            : null,
      );
    } catch (err) {
      setPackagesError(err.message || "Unable to load studio packages.");
      setSessions([]);
      setSessionId(null);
    } finally {
      setLoadingPackages(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const selectedSession = useMemo(() => {
    return sessions.find((s) => Number(s.id) === Number(sessionId)) || null;
  }, [sessions, sessionId]);

  const onChange = (key) => (e) => {
    updateField(key, e.target.value);

    if (isErrorOpen) {
      closeError();
    }
  };

  const validateForm = () => {
    if (!selectedSession) return "Please select a package.";
    if (!date) return "Please choose a date.";
    if (!time) return "Please choose a time.";
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.trim()) return "Please enter your email address.";

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) return "Please enter a valid email address.";

    if (unavailableTimes.includes(time)) {
      return "This time slot is no longer available.";
    }

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      showError(error);
      return;
    }

    if (submitting || !selectedSession) return;

    try {
      setSubmitting(true);
      closeError();

      const payload = {
        package_id: selectedSession.id,
        booking_date: date,
        booking_time: time,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim(),
      };

      const res = await apiFetch("create_studio_session_payment", payload);

      if (!res?.success) {
        throw new Error(res?.message || "Unable to start payment.");
      }

      if (!res?.checkout_url) {
        throw new Error("Payment link was not returned.");
      }

      window.location.href = res.checkout_url;
    } catch (err) {
      showError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const loadUnavailableTimes = useCallback(async () => {
    if (!date || !selectedSession?.id) {
      setUnavailableTimes([]);
      return;
    }

    try {
      setLoadingTimes(true);

      const res = await apiFetch("get_studio_booked_times", {
        booking_date: date,
        package_id: selectedSession.id,
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load booked times.");
      }

      const booked = Array.isArray(res.data) ? res.data : [];
      setUnavailableTimes(booked);

      setTime((prev) => (booked.includes(prev) ? "" : prev));
    } catch (err) {
      setUnavailableTimes([]);
      showError(err.message || "Failed to load booked times.");
    } finally {
      setLoadingTimes(false);
    }
  }, [apiFetch, date, selectedSession, showError]);

  useEffect(() => {
    loadUnavailableTimes();
  }, [loadUnavailableTimes]);

  const isPastTime = (slot) => {
    if (!date) return false;

    const nowUK = new Date(
      new Date().toLocaleString("en-GB", {
        timeZone: "Europe/London",
      }),
    );

    const todayUKStr = new Date().toLocaleDateString("en-CA", {
      timeZone: "Europe/London",
    });

    const isToday = date === todayUKStr;
    if (!isToday) return false;

    const [hour, minute] = slot.split(":").map(Number);

    const slotTime = new Date(nowUK);
    slotTime.setHours(hour, minute, 0, 0);

    return slotTime <= nowUK;
  };

  const today = new Date().toLocaleDateString("en-CA", {
    timeZone: "Europe/London",
  });

  const formatDate = (value) => {
    if (!value) return "—";
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  };

  return (
    <>
      <main className="min-h-screen bg-[#0b0b0c] text-white">
        <style>{`
          input[type="date"]::-webkit-calendar-picker-indicator {
            opacity: 0;
            position: absolute;
            right: 0;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          input[type="date"] {
            color-scheme: dark;
          }
        `}</style>

        <section className="relative overflow-hidden border-b border-white/10 bg-[radial-gradient(circle_at_top,_rgba(236,72,153,0.18),_transparent_30%),radial-gradient(circle_at_right,_rgba(255,255,255,0.06),_transparent_25%),linear-gradient(to_bottom,_#111214,_#090909)]">
          <div className="mx-auto max-w-7xl px-5 pt-32 pb-20">
            <div className="max-w-3xl">
              <h1 className="mt-6 text-4xl font-semibold tracking-tight sm:text-5xl md:text-6xl">
                Book your studio session with ease
              </h1>

              <p className="mt-5 max-w-2xl text-base leading-7 text-white/70 md:text-lg">
                Select your package, pick a convenient date and time, and
                continue to payment in a few simple steps.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#0f1012]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <div className="mb-8">
              <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                Step 1
              </p>
              <h2 className="mt-2 text-3xl font-semibold">Choose a package</h2>
            </div>

            {loadingPackages ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                Loading packages...
              </div>
            ) : packagesError ? (
              <div className="rounded-3xl border border-red-500/20 bg-red-500/10 p-8 text-red-200">
                {packagesError}
              </div>
            ) : sessions.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                No studio packages are available right now.
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                {sessions.map((s) => {
                  const active = Number(s.id) === Number(sessionId);

                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        setSessionId(s.id);
                        setTime("");

                        if (isErrorOpen) {
                          closeError();
                        }
                      }}
                      className={[
                        "group relative overflow-hidden rounded-3xl border p-6 text-left transition-all duration-300",
                        active
                          ? "border-pink-500 bg-gradient-to-b from-pink-500/20 to-white/5 shadow-[0_0_0_1px_rgba(236,72,153,0.25),0_20px_50px_rgba(0,0,0,0.35)]"
                          : "border-white/10 bg-white/[0.03] hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.05]",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm uppercase tracking-[0.2em] text-white/45">
                            Session
                          </p>
                          <h3 className="mt-2 text-2xl font-semibold">
                            {s.name}
                          </h3>
                        </div>

                        <div
                          className={[
                            "rounded-full px-3 py-1 text-xs font-semibold",
                            active
                              ? "bg-pink-500 text-white"
                              : "bg-white/10 text-white/70",
                          ].join(" ")}
                        >
                          {money(s.price)}
                        </div>
                      </div>

                      <div className="mt-8 space-y-3 text-sm text-white/75">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-pink-400" />
                          <span>{s.duration}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-pink-400" />
                          <span>{s.editedImages}</span>
                        </div>

                        {s.extra && (
                          <div className="flex items-start gap-2">
                            <span className="mt-1 h-2 w-2 rounded-full bg-pink-400" />
                            <span>{s.extra}</span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#0b0b0c]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <div className="mb-8">
              <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                Step 2
              </p>
              <h2 className="mt-2 text-3xl font-semibold">Choose your slot</h2>
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                <label className="text-sm font-medium text-white/70">
                  Preferred date
                </label>

                <div className="relative mt-3">
                  <input
                    type="date"
                    value={date}
                    min={today}
                    onChange={(e) => {
                      setDate(e.target.value);
                      setTime("");

                      if (isErrorOpen) {
                        closeError();
                      }
                    }}
                    className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 pr-10 text-white outline-none transition focus:border-pink-500"
                  />

                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-white/80"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                <label className="text-sm font-medium text-white/70">
                  Preferred time
                </label>

                {!date ? (
                  <p className="mt-3 text-sm text-white/50">
                    Select a date first.
                  </p>
                ) : loadingTimes ? (
                  <p className="mt-3 text-sm text-white/50">
                    Checking available times...
                  </p>
                ) : (
                  <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {timeSlots.map((slot) => {
                      const active = time === slot;
                      const disabled =
                        unavailableTimes.includes(slot) || isPastTime(slot);

                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => {
                            if (disabled) return;
                            setTime(slot);

                            if (isErrorOpen) {
                              closeError();
                            }
                          }}
                          disabled={disabled}
                          className={[
                            "rounded-2xl border px-4 py-3 text-sm font-semibold transition",
                            disabled
                              ? "cursor-not-allowed border-white/5 bg-white/[0.03] text-white/25 line-through"
                              : active
                                ? "border-pink-500 bg-pink-500 text-white shadow-lg shadow-pink-500/20"
                                : "border-white/10 bg-black/20 text-white/80 hover:border-white/20 hover:bg-white/5",
                          ].join(" ")}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#0f1012]">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-12">
            <form
              onSubmit={onSubmit}
              className="space-y-5 rounded-3xl border border-white/10 bg-white/[0.03] p-7 lg:col-span-7"
            >
              <div>
                <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                  Step 3
                </p>
                <h2 className="mt-2 text-3xl font-semibold">Your details</h2>
              </div>

              <input
                placeholder="Full name"
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-white/35 outline-none transition focus:border-pink-500"
                value={form.name}
                onChange={onChange("name")}
                required
              />

              <input
                placeholder="Email address"
                type="email"
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-white/35 outline-none transition focus:border-pink-500"
                value={form.email}
                onChange={onChange("email")}
                required
              />

              <input
                placeholder="Phone number"
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-white/35 outline-none transition focus:border-pink-500"
                value={form.phone}
                onChange={onChange("phone")}
              />

              <textarea
                rows={5}
                placeholder="Notes"
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-white/35 outline-none transition focus:border-pink-500"
                value={form.notes}
                onChange={onChange("notes")}
              />
            </form>

            <aside className="lg:col-span-5">
              <div className="sticky top-6 rounded-3xl border border-pink-500/20 bg-gradient-to-b from-[#181116] to-[#0d0b0d] p-7 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                  Summary
                </p>
                <h3 className="mt-2 text-2xl font-semibold">
                  Booking overview
                </h3>

                {!selectedSession ? (
                  <div className="mt-8 text-sm text-white/70">
                    No package selected.
                  </div>
                ) : (
                  <div className="mt-8 space-y-4 text-sm text-white/75">
                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Package</span>
                      <span className="text-right font-medium text-white">
                        {selectedSession.name}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Session</span>
                      <span className="text-right font-medium text-white">
                        {selectedSession.duration}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Edited Images</span>
                      <span className="text-right font-medium text-white">
                        {selectedSession.editedImages}
                      </span>
                    </div>

                    {selectedSession.extra && (
                      <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                        <span>Extra</span>
                        <span className="text-right font-medium text-white">
                          {selectedSession.extra}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Date</span>
                      <span className="text-right font-medium text-white">
                        {formatDate(date)}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Time</span>
                      <span className="text-right font-medium text-white">
                        {time || "—"}
                      </span>
                    </div>

                    <div className="mt-6 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-4">
                      <span className="text-base font-semibold text-white">
                        Total
                      </span>
                      <span className="text-2xl font-semibold text-pink-400">
                        {money(selectedSession.price)}
                      </span>
                    </div>

                    <button
                      type="submit"
                      onClick={onSubmit}
                      disabled={submitting || loadingPackages || loadingTimes}
                      className={[
                        "w-full rounded-2xl px-6 py-4 text-base font-semibold text-white transition",
                        submitting || loadingPackages || loadingTimes
                          ? "cursor-not-allowed bg-pink-500/40 text-white/70"
                          : "bg-pink-500 hover:bg-pink-400",
                      ].join(" ")}
                    >
                      {submitting ? "Starting payment..." : "Continue to Payment"}
                    </button>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>
      </main>

      <ErrorModal
        open={isErrorOpen}
        message={errorMessage}
        onClose={closeError}
        autoCloseMs={4000}
      />
    </>
  );
}